/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
/**
 * Cài đặt › Module › Hosting › a cPanel/WHM connection › Trang nghiệp vụ
 * (subsystems/hosting/facade/adapters/admin-api): what the connection's panel holds, as HostBill's
 * "Servers" and "Accounts on server" show it — the servers and their room (new accounts on or
 * off, with a reason), the accounts the panel holds with the ones not in the system yet, and the
 * panel's packages (what a plan's provider plan may name). Servers are read from our records; the
 * accounts and packages are read at the panel on request (each read is a line of Nhật ký).
 */
import { Play, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { api } from '../../api';
import type { HostingServer, PanelAccount, PanelPackage } from '../../api/areas/hosting';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Field, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { Loading } from '../../components/States';
import { useWrite, WriteError } from '../../components/Write';
import { formatCount, formatDateTime } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { ActionDialog, FormFooter, ReasonAction, ReasonField, type DialogBodyProps } from '../operator/shared';
import { Panel } from '../integrations/shared';
import { CustomerRef } from '../domains/refs';

type Loose = (key: string, o?: Record<string, unknown>) => string;

export function HostingInstancePanel({ module, instanceKey, canManage }: { module: string; instanceKey: string; canManage: boolean }) {
  const { t } = useTranslation('modules');
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{t('hosting.intro')}</p>
      <ServersPanel module={module} instanceKey={instanceKey} canManage={canManage} />
      <AccountsPanel module={module} instanceKey={instanceKey} />
      <PackagesPanel module={module} instanceKey={instanceKey} />
    </div>
  );
}

function ServersPanel({ module, instanceKey, canManage }: { module: string; instanceKey: string; canManage: boolean }) {
  const { t } = useTranslation('modules');
  const list = useApi((signal) => api.listHostingServers(module, instanceKey, { signal }), [module, instanceKey]);
  const columns: Column<HostingServer>[] = [
    { key: 'server', header: t('hosting.serverCol.server'), cell: (s) => <code className="text-xs">{s.server}</code> },
    {
      key: 'accounts',
      header: t('hosting.serverCol.accounts'),
      cell: (s) => (
        <span className="grid">
          <span>{t('hosting.accountsOf', { accounts: formatCount(s.accounts), max: s.max_accounts ? formatCount(s.max_accounts) : '∞' })}</span>
          {s.pending ? <span className="text-xs text-muted-foreground">{t('hosting.pendingOf', { count: s.pending })}</span> : null}
        </span>
      ),
    },
    { key: 'disk', header: t('hosting.serverCol.disk'), cell: (s) => t('hosting.diskGb', { value: formatCount(s.disk_free_gb) }) },
    {
      key: 'up',
      header: t('hosting.serverCol.up'),
      cell: (s) => (s.gone_at ? <Badge tone="neutral">{t('hosting.gone')}</Badge> : <Badge tone={s.up ? 'ok' : 'crit'}>{s.up ? t('hosting.upYes') : t('hosting.upNo')}</Badge>),
    },
    { key: 'accepting', header: t('hosting.serverCol.accepting'), cell: (s) => <Badge tone={s.accepting ? 'ok' : 'warn'}>{s.accepting ? t('hosting.acceptingYes') : t('hosting.acceptingNo')}</Badge> },
    { key: 'checked', header: t('hosting.serverCol.checked'), cell: (s) => (s.checked_at ? formatDateTime(s.checked_at) : '—') },
    { key: 'note', header: t('hosting.serverCol.note'), cell: (s) => s.note ?? '—' },
  ];
  if (canManage) {
    columns.push({
      key: 'action',
      header: '',
      className: 'text-right',
      cell: (s) => (
        <ActionDialog label={s.accepting ? t('hosting.stop') : t('hosting.start')} title={s.accepting ? t('hosting.stopTitle', { server: s.server }) : t('hosting.startTitle', { server: s.server })} onChanged={list.reload}>
          {(p) => <AcceptingForm {...p} module={module} instanceKey={instanceKey} server={s} />}
        </ActionDialog>
      ),
    });
  }
  return (
    <Panel
      title={t('hosting.servers')}
      description={t('hosting.serversHint')}
      flush
      actions={
        canManage ? (
          <ReasonAction
            label={t('hosting.refresh')}
            icon={RefreshCw}
            title={t('hosting.refreshTitle')}
            description={t('hosting.refreshIntro')}
            run={(reason, opts) => api.refreshHostingServers(module, instanceKey, { reason }, opts)}
            done={() => t('hosting.refreshDone')}
            onChanged={list.reload}
          />
        ) : undefined
      }
    >
      {list.status === 'loading' ? <Loading rows={2} /> : null}
      {list.status === 'error' ? <DataError error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'success' ? <DataTable columns={columns} rows={list.data.items} rowKey={(s) => s.server} empty={<p className="px-5 pb-5 text-sm text-muted-foreground">{t('hosting.serversNone')}</p>} /> : null}
    </Panel>
  );
}

function AcceptingForm({ close, changed, module, instanceKey, server }: DialogBodyProps & { module: string; instanceKey: string; server: HostingServer }) {
  const { t } = useTranslation('modules');
  const [note, setNote] = useState(server.note ?? '');
  const [reason, setReason] = useState('');
  const accepting = !server.accepting;
  // The whole body: an Idempotency-Key per distinct request (a note changed is a new one).
  const w = useWrite((b: { accepting: boolean; note: string | null; reason: string }, opts) => api.setHostingServerAccepting(module, instanceKey, server.server, b, opts));
  const done = w.state.phase === 'done';
  const valid = reason.trim().length > 0 && note.length <= 500;
  return (
    <form
      className="grid gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && !w.busy && !done) void w.run({ accepting, note: note.trim() || null, reason: reason.trim() }).then((r) => r && changed());
      }}
    >
      {done ? (
        <Callout tone="ok">{t('hosting.acceptingDone')}</Callout>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('hosting.acceptingIntro')}</p>
          <Field label={t('hosting.note')}>
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </Field>
          <ReasonField value={reason} onChange={setReason} />
        </>
      )}
      {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
      <FormFooter busy={w.busy} disabled={!valid} submit={done ? undefined : accepting ? t('hosting.start') : t('hosting.stop')} onClose={close} danger={!accepting} />
    </form>
  );
}

/** The panel's accounts, read at the panel when staff ask (each page is one call). */
function AccountsPanel({ module, instanceKey }: { module: string; instanceKey: string }) {
  const { t } = useTranslation('modules');
  const loose = t as unknown as Loose;
  const [cursor, setCursor] = useState<string | null>(null);
  const [asked, setAsked] = useState(false);
  const page = useApi((signal) => (asked ? api.listPanelAccounts(module, instanceKey, { cursor }, { signal }) : Promise.resolve(null)), [module, instanceKey, cursor, asked]);
  const columns: Column<PanelAccount>[] = [
    { key: 'username', header: t('hosting.accountCol.username'), cell: (a) => <code className="text-xs">{a.username}</code> },
    { key: 'domain', header: t('hosting.accountCol.domain'), cell: (a) => a.domain },
    { key: 'status', header: t('hosting.accountCol.status'), cell: (a) => <Badge tone={a.status === 'ACTIVE' ? 'ok' : a.status === 'SUSPENDED' ? 'serious' : 'neutral'} title={a.status}>{loose(`hosting.status.${a.status}`, { defaultValue: a.status })}</Badge> },
    { key: 'plan', header: t('hosting.accountCol.plan'), cell: (a) => a.plan ?? '—' },
    {
      key: 'service',
      header: t('hosting.accountCol.service'),
      cell: (a) =>
        a.service_id ? (
          <Link className="text-primary hover:underline" to={`/sales?tab=subscriptions&service=${encodeURIComponent(a.service_id)}`}>
            {t('hosting.openService')}
          </Link>
        ) : (
          <Badge tone="warn">{t('hosting.notInSystem')}</Badge>
        ),
    },
    { key: 'customer', header: t('hosting.accountCol.customer'), cell: (a) => <CustomerRef id={a.customer_id} name={null} /> },
  ];
  const data = page.status === 'success' ? page.data : null;
  return (
    <Panel
      title={t('hosting.accounts')}
      description={t('hosting.accountsHint')}
      flush
      actions={
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setCursor(null);
            if (asked) page.reload();
            else setAsked(true);
          }}
        >
          <Play aria-hidden="true" /> {t('hosting.load')}
        </Button>
      }
    >
      {asked && page.status === 'loading' ? <Loading rows={3} /> : null}
      {page.status === 'error' ? <DataError error={page.error} onRetry={page.reload} /> : null}
      {data ? (
        <>
          <DataTable columns={columns} rows={data.items} rowKey={(a) => a.username} />
          {data.next ? (
            <div className="flex justify-end border-t p-4">
              <Button variant="outline" size="sm" onClick={() => setCursor(data.next)}>
                {t('hosting.more')}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </Panel>
  );
}

/** The panel's packages, read at the panel when staff ask. */
function PackagesPanel({ module, instanceKey }: { module: string; instanceKey: string }) {
  const { t } = useTranslation('modules');
  const loose = t as unknown as Loose;
  const [asked, setAsked] = useState(false);
  const list = useApi((signal) => (asked ? api.listPanelPackages(module, instanceKey, { signal }) : Promise.resolve(null)), [module, instanceKey, asked]);
  const columns: Column<PanelPackage>[] = [
    { key: 'code', header: t('hosting.packageCol.code'), cell: (p) => <code className="text-xs">{p.code}</code> },
    { key: 'name', header: t('hosting.packageCol.name'), cell: (p) => p.name },
    {
      key: 'limits',
      header: t('hosting.packageCol.limits'),
      cell: (p) => p.limits.map((l) => `${loose(`hosting.meter.${l.meter}`, { defaultValue: l.meter })}: ${l.limit == null ? t('hosting.unlimited') : formatCount(l.limit)}`).join(' · '),
    },
  ];
  return (
    <Panel
      title={t('hosting.packages')}
      description={t('hosting.packagesHint')}
      flush
      actions={
        <Button size="sm" variant="outline" onClick={() => (asked ? list.reload() : setAsked(true))}>
          <Play aria-hidden="true" /> {t('hosting.loadPackages')}
        </Button>
      }
    >
      {asked && list.status === 'loading' ? <Loading rows={2} /> : null}
      {list.status === 'error' ? <DataError error={list.error} onRetry={list.reload} /> : null}
      {list.status === 'success' && list.data ? <DataTable columns={columns} rows={list.data.items} rowKey={(p) => p.name} /> : null}
    </Panel>
  );
}
